// ─────────────────────────────────────────────────────────────
// FORMATTING + TRADE CALCULATION UTILITIES
// Pure functions — no React, no Supabase, no DOM.
// ─────────────────────────────────────────────────────────────

import { getTickInfo } from "./instruments";

export function fmt$(n) {
  if (n === null || n === undefined) return "—";
  return `${n >= 0 ? "+" : "-"}$${Math.abs(n).toFixed(2)}`;
}

export function fmtPct(n) {
  return `${(n * 100).toFixed(1)}%`;
}

// ─────────────────────────────────────────────────────────────
// SESSION SHIFT DETECTION
//
// CME futures sessions run roughly 18:00 ET prior day → 17:00 ET
// current day. So a trade entered at 22:30 on Monday belongs to
// the "Tuesday" session in exchange terms.
//
// We classify a trade as "session-shifted" when its entry_time is
// 18:00 or later — i.e. the trade was entered in the evening
// preceding its session day. The user-visible cue (a 🌙 icon) helps
// users understand why a Monday-evening trade shows a Tuesday date.
// ─────────────────────────────────────────────────────────────
export function isSessionShifted(trade) {
  if (!trade?.entry_time) return false;
  const [hh] = String(trade.entry_time).split(":");
  const hour = Number(hh);
  if (Number.isNaN(hour)) return false;
  return hour >= 18; // 6 PM ET or later → entered the prior evening
}

// Returns the wall-clock calendar date a trade was actually entered on,
// derived from session-shifted detection. For shifted trades, this is
// the day BEFORE trade_date.
export function getEnteredCalendarDate(trade) {
  if (!trade?.trade_date) return null;
  if (!isSessionShifted(trade)) return trade.trade_date;
  // Subtract one day from trade_date
  const [y, m, d] = trade.trade_date.split("-").map(Number);
  if (!y || !m || !d) return trade.trade_date;
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() - 1);
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth()+1).padStart(2,"0")}-${String(dt.getUTCDate()).padStart(2,"0")}`;
}

// Returns the YYYY-MM-DD string for the CME futures session day that is
// currently "open" given the user's local clock. Sessions roll over at
// 18:00 (6 PM) local time:
//   - Before 18:00 → today's calendar date
//   - At/after 18:00 → tomorrow's calendar date (next session is open)
//
// This is what "TODAY" should mean on a futures trader's dashboard:
// the session you're trading in right now.
export function getActiveSessionDate(now = new Date()) {
  const next = new Date(now);
  if (now.getHours() >= 18) {
    next.setDate(next.getDate() + 1);
  }
  const y = next.getFullYear();
  const m = String(next.getMonth() + 1).padStart(2, "0");
  const d = String(next.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

// Returns a single trade's contribution to a P&L total, with the user's
// preferred convention applied:
//   - "gross" (default for strategy analysis): trade.pnl as-is
//   - "net" (for balance-relevant aggregations): pnl - fees - commissions
//
// This is the single source of truth used by the analytics page's
// gross/net toggle and by anywhere else that needs to sum P&L with a
// configurable convention.
export function tradeAmount(trade, mode = "gross") {
  const gross = Number(trade?.pnl) || 0;
  if (mode !== "net") return gross;
  const fees = Number(trade?.fees) || 0;
  const comm = Number(trade?.commissions) || 0;
  return gross - fees - comm;
}

// ─────────────────────────────────────────────────────────────
// DISCIPLINE SCORE
// ─────────────────────────────────────────────────────────────
export function calcDisciplineScore(trade) {
  const ruleAdherence = Number(trade.rule_adherence   ?? 5);
  const impulsiveness = Number(trade.impulsiveness    ?? 5);
  const conviction    = Number(trade.conviction_level ?? 5);
  const focus         = Number(trade.focus_level      ?? 5);
  const fear          = Number(trade.fear_level       ?? 5);
  const greed         = Number(trade.greed_level      ?? 5);
  return Math.round(
    (ruleAdherence + (10 - impulsiveness) + conviction + focus + (10 - Math.max(fear, greed))) / 5
  );
}

// ─────────────────────────────────────────────────────────────
// NORMALISE TRADE — single source of truth for result + discipline_score
//
// P&L handling:
//   - If data.pnl is supplied (CSV import, manual entry where user
//     typed a value), TRUST IT. Do not silently overwrite the
//     broker's authoritative number.
//   - If data.pnl is missing AND we have entry/exit/size, compute
//     using real futures tick math:
//         ticks = (exit - entry) / tickSize
//         gross = direction * ticks * tickValue * size
//   - "P&L" here is GROSS (matches broker conventions). Fees and
//     commissions are stored separately; net = pnl - fees - commissions
//     can be computed at display time.
// ─────────────────────────────────────────────────────────────

function computeFuturesPnl(trade, instrumentSettings = []) {
  const entry = parseFloat(trade.entry_price);
  const exit  = parseFloat(trade.exit_price);
  const size  = parseFloat(trade.position_size) || 1;
  if (isNaN(entry) || isNaN(exit) || !size) return null;

  const { tickSize, tickValue } = getTickInfo(trade.symbol || "", instrumentSettings);
  if (!tickSize || !tickValue) return null;

  const dir   = String(trade.direction || "").toLowerCase().startsWith("s") ? -1 : 1;
  const ticks = (exit - entry) / tickSize;
  const gross = dir * ticks * tickValue * size;
  return parseFloat(gross.toFixed(2));
}

export function normaliseTrade(data, instrumentSettings = []) {
  // Convert empty strings to null for numeric fields to avoid Supabase validation errors
  const numericFields = [
    'entry_price', 'exit_price', 'position_size', 'stop_loss', 'take_profit',
    'conviction_level', 'fear_level', 'greed_level', 'focus_level',
    'rule_adherence', 'impulsiveness', 'energy_level', 'market_context',
    'outcome_satisfaction', 'confidence_level', 'emotional_intensity', 'commissions', 'fees'
  ];

  const cleaned = { ...data };
  numericFields.forEach(field => {
    if (cleaned[field] === '' || cleaned[field] === undefined) {
      cleaned[field] = null;
    } else {
      const parsed = parseFloat(cleaned[field]);
      cleaned[field] = isNaN(parsed) ? null : parsed;
    }
  });

  // P&L: trust supplied value if present, else compute from futures math.
  // "Supplied" = data.pnl is a finite number, not undefined/null/""/NaN.
  const suppliedPnl  = data.pnl;
  const suppliedNum  = parseFloat(suppliedPnl);
  const hasSupplied  = suppliedPnl !== undefined
                    && suppliedPnl !== null
                    && suppliedPnl !== ""
                    && !isNaN(suppliedNum);

  let pnl;
  if (hasSupplied) {
    pnl = suppliedNum;
  } else {
    const computed = computeFuturesPnl(cleaned, instrumentSettings);
    pnl = computed !== null ? computed : 0;
  }

  return {
    ...cleaned,
    pnl: parseFloat(pnl.toFixed(2)),
    result: pnl > 0 ? "Win" : pnl < 0 ? "Loss" : "Breakeven",
    discipline_score: calcDisciplineScore(cleaned),
  };
}
